export type AppEnvironment = 'development' | 'staging' | 'production';
export type Severity = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

export type RuntimeEnv = Record<string, string | undefined>;

export interface ObservabilityConfig {
  service?: string;
  version?: string;
  environment?: AppEnvironment | string;
  dsn?: string;
  enabled?: boolean;
  release?: string;
  sendDefaultPii?: boolean;
  tracesSampleRate?: number;
  profilesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
  enableLogs?: boolean;
  debug?: boolean;
  env?: RuntimeEnv;
  beforeSend?: (event: unknown, hint?: unknown) => unknown | null;
}

export interface ResolvedObservabilityConfig
  extends Omit<ObservabilityConfig, 'environment' | 'enabled' | 'release' | 'sendDefaultPii'> {
  environment: AppEnvironment;
  enabled: boolean;
  release?: string;
  sendDefaultPii: boolean;
}

export type LogContext = Record<string, unknown>;

export interface ErrorContext extends LogContext {
  event?: string;
  requestId?: string;
  userId?: string;
  status?: number;
}

export interface NormalizedError {
  name: string;
  message: string;
  stack?: string;
  status?: number;
  code?: string;
  expose: boolean;
  severity: Severity;
  context?: LogContext;
}

export interface ErrorResponse {
  code: number;
  message: string;
  errors?: unknown;
  requestId?: string;
  stack?: string;
}

export interface AppErrorOptions {
  status?: number;
  code?: string;
  expose?: boolean;
  severity?: Severity;
  context?: LogContext;
  cause?: unknown;
  errors?: unknown;
}

type ErrorReporter = (error: unknown, context?: ErrorContext) => string | null | undefined;
type UserSetter = (user: ObservabilityUser | null) => void;

export interface ObservabilityUser {
  id?: string;
  email?: string;
  username?: string;
  ip_address?: string;
  [key: string]: unknown;
}

let errorReporter: ErrorReporter | undefined;
let userSetter: UserSetter | undefined;
let pendingUser: ObservabilityUser | null | undefined;

const SENSITIVE_KEY_PATTERN =
  /(authorization|cookie|set-cookie|token|secret|password|passwd|api[-_]?key|apikey|jwt|session|csrf|private[-_]?key)/i;

export class AppError extends Error {
  status: number;
  code?: string;
  expose: boolean;
  severity: Severity;
  context?: LogContext;
  cause?: unknown;
  errors?: unknown;

  constructor(message: string, options: AppErrorOptions = {}) {
    super(message);
    this.name = 'AppError';
    this.status = options.status ?? 500;
    this.code = options.code;
    this.expose = options.expose ?? this.status < 500;
    this.severity = options.severity ?? severityFromStatus(this.status);
    this.context = options.context;
    this.cause = options.cause;
    this.errors = options.errors;
  }
}

export function setErrorReporter(reporter?: ErrorReporter): void {
  errorReporter = reporter;
}

export function setObservabilityUserSetter(setter?: UserSetter): void {
  userSetter = setter;

  if (setter && pendingUser !== undefined) {
    setter(pendingUser);
  }
}

export function setObservabilityUser(user: ObservabilityUser | null): void {
  pendingUser = user;

  if (userSetter) {
    userSetter(user);
  }
}

export function reportError(error: unknown, context?: ErrorContext): string | null {
  if (!errorReporter) {
    return null;
  }

  return errorReporter(error, sanitize(context) as ErrorContext) ?? null;
}

export function normalizeEnvironment(value?: string, fallback: AppEnvironment = 'development'): AppEnvironment {
  const normalized = String(value || '').trim().toLowerCase();

  if (['prod', 'production', 'prd', 'main'].includes(normalized)) {
    return 'production';
  }

  if (['stg', 'stage', 'staging'].includes(normalized)) {
    return 'staging';
  }

  if (['dev', 'develop', 'development', 'local', 'test'].includes(normalized)) {
    return 'development';
  }

  return fallback;
}

export function resolveObservabilityConfig(config: ObservabilityConfig = {}): ResolvedObservabilityConfig {
  const env = config.env ?? getRuntimeEnv();
  const environment = normalizeEnvironment(
    firstDefined([
      stringify(config.environment),
      env.SENTRY_ENVIRONMENT,
      env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
      env.REACT_APP_SENTRY_ENVIRONMENT,
      env.REACT_APP_NODE_ENV,
      env.NODE_ENV
    ]),
    env.NODE_ENV === 'production' ? 'production' : 'development'
  );

  const dsn =
    config.dsn ??
    firstDefined([
      env.SENTRY_DSN,
      env.NEXT_PUBLIC_SENTRY_DSN,
      env.REACT_APP_SENTRY_DSN
    ]);

  const service =
    config.service ??
    firstDefined([
      env.SERVICE_NAME,
      env.APP_NAME,
      env.NEXT_PUBLIC_SERVICE_NAME,
      env.REACT_APP_SERVICE_NAME,
      env.npm_package_name
    ]);

  const version =
    config.version ??
    firstDefined([
      env.APP_VERSION,
      env.NEXT_PUBLIC_APP_VERSION,
      env.REACT_APP_APP_VERSION,
      env.npm_package_version
    ]);

  const explicitEnabled =
    config.enabled ??
    parseOptionalBoolean(
      firstDefined([
        env.SENTRY_ENABLED,
        env.NEXT_PUBLIC_SENTRY_ENABLED,
        env.REACT_APP_SENTRY_ENABLED
      ])
    );

  const release =
    config.release ??
    env.SENTRY_RELEASE ??
    (service && version ? `${service}@${version}` : version);
  const isSentryEnvironment = environment === 'staging' || environment === 'production';

  return {
    ...config,
    service,
    version,
    environment,
    dsn,
    enabled: Boolean(dsn && isSentryEnvironment && (explicitEnabled ?? true)),
    release,
    sendDefaultPii: config.sendDefaultPii ?? parseOptionalBoolean(env.SENTRY_SEND_DEFAULT_PII) ?? true,
    tracesSampleRate:
      config.tracesSampleRate ??
      parseOptionalNumber(
        firstDefined([
          env.SENTRY_TRACES_SAMPLE_RATE,
          env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
          env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE
        ])
      ),
    profilesSampleRate:
      config.profilesSampleRate ??
      parseOptionalNumber(env.SENTRY_PROFILES_SAMPLE_RATE),
    replaysSessionSampleRate:
      config.replaysSessionSampleRate ??
      parseOptionalNumber(
        firstDefined([
          env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
          env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
        ])
      ),
    replaysOnErrorSampleRate:
      config.replaysOnErrorSampleRate ??
      parseOptionalNumber(
        firstDefined([
          env.NEXT_PUBLIC_SENTRY_REPLAYS_ERROR_SAMPLE_RATE,
          env.REACT_APP_SENTRY_REPLAYS_ERROR_SAMPLE_RATE
        ])
      ),
    enableLogs:
      config.enableLogs ??
      parseOptionalBoolean(
        firstDefined([
          env.SENTRY_ENABLE_LOGS,
          env.NEXT_PUBLIC_SENTRY_ENABLE_LOGS,
          env.REACT_APP_SENTRY_ENABLE_LOGS
        ])
      ),
    debug: config.debug ?? parseOptionalBoolean(env.SENTRY_DEBUG)
  };
}

export function normalizeError(error: unknown, context?: ErrorContext): NormalizedError {
  if (error instanceof AppError) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      status: error.status,
      code: error.code,
      expose: error.expose,
      severity: error.severity,
      context: sanitize({ ...error.context, ...context }) as LogContext
    };
  }

  if (isErrorLike(error)) {
    const status = getErrorStatus(error);

    return {
      name: stringify(error.name) || 'Error',
      message: stringify(error.message) || 'Unknown error',
      stack: stringify(error.stack),
      status,
      code: stringify(error.code),
      expose: Boolean(error.expose ?? error.isPublic ?? (status && status < 500)),
      severity: severityFromStatus(status),
      context: sanitize(context) as LogContext
    };
  }

  if (typeof error === 'string') {
    return {
      name: 'Error',
      message: error,
      expose: false,
      severity: 'error',
      context: sanitize(context) as LogContext
    };
  }

  return {
    name: 'NonErrorException',
    message: safeStringify(error),
    expose: false,
    severity: 'error',
    context: sanitize(context) as LogContext
  };
}

export function sanitize<T>(value: T, maxDepth = 5): T {
  return sanitizeValue(value, maxDepth, new WeakSet()) as T;
}

export function safeStringify(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(sanitize(value));
  } catch (_error) {
    return String(value);
  }
}

export function parseOptionalBoolean(value?: string | boolean): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const normalized = String(value).trim().toLowerCase();

  if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
    return false;
  }

  return undefined;
}

export function parseOptionalNumber(value?: string | number): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function getRuntimeEnv(): RuntimeEnv {
  const maybeProcess = (globalThis as unknown as { process?: { env?: RuntimeEnv } }).process;
  return maybeProcess?.env ?? {};
}

export function severityFromStatus(status?: number): Severity {
  if (!status) {
    return 'error';
  }

  if (status >= 500) {
    return 'error';
  }

  if (status >= 400) {
    return 'warning';
  }

  return 'info';
}

function getErrorStatus(error: Record<string, unknown>): number | undefined {
  const status = Number(error.status ?? error.statusCode);
  return Number.isFinite(status) ? status : undefined;
}

function isErrorLike(value: unknown): value is Error & Record<string, unknown> {
  return (
    value instanceof Error ||
    (value !== null &&
      typeof value === 'object' &&
      ('message' in value || 'stack' in value || 'name' in value))
  );
}

function sanitizeValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (depth < 0) {
    return '[Truncated]';
  }

  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    return normalizeError(value);
  }

  if (seen.has(value)) {
    return '[Circular]';
  }

  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, depth - 1, seen));
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((result, [key, item]) => {
    result[key] = SENSITIVE_KEY_PATTERN.test(key) ? '[Redacted]' : sanitizeValue(item, depth - 1, seen);
    return result;
  }, {});
}

function firstDefined(values: Array<string | undefined>): string | undefined {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function stringify(value: unknown): string | undefined {
  return typeof value === 'string' ? value : value === undefined || value === null ? undefined : String(value);
}
